import { useQuery } from "@tanstack/react-query";
import { Button } from "@tcg/ui";
import { useRealtime } from "@tcg/realtime/react";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Camera,
  ListChecks,
  PauseCircle,
  ScanLine,
  Stamp,
  TriangleAlert,
  UserRound,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { useAuthState, useCan, usePos, useRegister } from "@/app/context";
import { useOnlineStatus } from "@/hooks/use-online-status";
import { call } from "@/lib/api";
import { minutesSince } from "@/lib/snapshot";
import { POS_TOPICS } from "@/lib/sse";

import { HoldModal } from "@/features/holds/HoldModal";

import { CartPane, type PayChoice } from "./CartPane";
import { CustomerModal } from "./CustomerModal";
import { useCart } from "./hooks/useCart";
import { useScannerSettings } from "@/lib/scanner/settings";

import { useScanInput } from "./hooks/useScanInput";
import type { TileItem } from "./ResultTile";
import { SearchPane } from "./SearchPane";

/**
 * 販売 (design/05 §3.1, T07-23): one scan / search field, results on the left, the basket on the
 * right, 客 / 保留(n) / 免税 on top. A cash sale of one card is 3 taps: scan (or tap the tile) →
 * 現金 → 受け取る (the tender screen opens with the exact amount chosen).
 */
export function SaleScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { api, db } = usePos();
  const register = useRegister();
  const cart = useCart();
  const online = useOnlineStatus();
  const [query, setQuery] = useState("");
  const [customerOpen, setCustomerOpen] = useState(false);
  const [holdOpen, setHoldOpen] = useState(false);

  // no scanning behind the lock screen (the PIN comes first)
  const unlocked = useAuthState().kind === "unlocked";
  const scanner = useScannerSettings();
  const scanInput = useScanInput({
    prefix: scanner.prefix,
    terminator: scanner.suffix,
    onScan: (code) => void cart.scan(code),
    onSearch: setQuery,
    enabled: unlocked,
  });

  const holds = useQuery({
    queryKey: ["pos-holds", "list", "active-count"],
    queryFn: () =>
      call(() => api.GET("/v1/pos/holds", { params: { query: { status: "active" } } })),
    staleTime: 30_000,
  });
  // 保留(n) follows the register's live stream (a hold made / resumed on another screen)
  useRealtime([POS_TOPICS.hold], () => void holds.refetch(), {
    onResync: () => void holds.refetch(),
  });

  const locationId = register.data?.register.locationId;
  const snapshotMeta = useLiveQuery(
    () => (locationId ? db.snapshotMeta.get(locationId) : undefined),
    [db, locationId],
  );
  const maxMinutes = register.data?.rules.offlineMaxMinutes ?? 5;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(timer);
  }, []);
  const snapshotMinutes = minutesSince(snapshotMeta?.fetchedAt, now);
  const snapshotOld = !online && snapshotMinutes !== null && snapshotMinutes > maxMinutes;

  const pay = useCallback(
    (choice: PayChoice) => {
      if (cart.canPay) {
        void navigate(`/pay?method=${choice}`);
      }
    },
    [cart.canPay, navigate],
  );

  // keyboard extras: F2 = search field, F9 = cash
  const scanField = scanInput.inputRef;
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "F2") {
        event.preventDefault();
        scanField.current?.focus();
      } else if (event.key === "F9") {
        event.preventDefault();
        pay("cash");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pay, scanField]);

  const onAdd = (item: TileItem) => void cart.addVariant({ variantId: item.variantId });
  const customer = cart.cart?.customer ?? null;
  const canTaxFree = useCan("tax.taxfree");
  const taxFreeOn = register.data?.features.taxFree !== false && canTaxFree;
  const holdCount = holds.data?.counts.active ?? 0;

  return (
    <div className="grid h-full min-h-0 grid-rows-[auto_auto_1fr]">
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-2">
        <label className="flex min-h-pos-touch min-w-[16rem] flex-1 items-center gap-2 rounded-md border bg-background px-3 focus-within:ring-2 focus-within:ring-ring">
          <ScanLine className="size-6 shrink-0" aria-hidden />
          <span className="sr-only">{t("sale.scan_label")}</span>
          <input
            {...scanInput.inputProps}
            type="search"
            inputMode="search"
            autoComplete="off"
            enterKeyHint="go"
            autoFocus
            placeholder={t("sale.scan_placeholder")}
            className="min-w-0 flex-1 bg-transparent text-xl outline-none"
          />
          <button
            type="button"
            aria-label={t("sale.camera")}
            title={t("sale.camera")}
            className="flex size-11 items-center justify-center rounded-md hover:bg-accent"
            disabled
          >
            <Camera aria-hidden className="size-6" />
          </button>
        </label>
        <Button
          variant="outline"
          size="pos"
          onClick={() => setCustomerOpen(true)}
          disabled={!cart.cart}
        >
          <UserRound aria-hidden />
          <span className="max-w-40 truncate">
            {t("customer.label")}:{" "}
            {customer ? (customer.name ?? customer.email ?? customer.phone) : t("customer.walk_in")}
          </span>
        </Button>
        <Button
          variant="outline"
          size="pos"
          onClick={() => setHoldOpen(true)}
          disabled={!cart.cart || cart.cart.lines.length === 0}
        >
          <PauseCircle aria-hidden />
          {t("sale.park")}
        </Button>
        <Button variant="outline" size="pos" onClick={() => void navigate("/hold")}>
          <ListChecks aria-hidden />
          {t("sale.quick_hold", { count: holdCount })}
        </Button>
        {taxFreeOn ? (
          <Button
            variant="outline"
            size="pos"
            onClick={() => void navigate("/tax-free")}
            disabled={!online}
            title={online ? undefined : t("taxfree.online_only")}
            aria-describedby={online ? undefined : "tax-free-offline"}
          >
            <Stamp aria-hidden />
            {t("sale.quick_tax_free")}
            {online ? null : (
              <span id="tax-free-offline" className="sr-only">
                {t("taxfree.online_only")}
              </span>
            )}
          </Button>
        ) : null}
      </div>
      {snapshotOld ? (
        <p role="alert" className="flex items-center gap-2 border-b bg-status-pending/20 px-3 py-2">
          <TriangleAlert aria-hidden className="size-5" />
          {t("sale.snapshot_old", { minutes: snapshotMinutes })}
        </p>
      ) : (
        <div />
      )}
      <div className="grid min-h-0 grid-cols-[minmax(0,1fr)_26rem] xl:grid-cols-[minmax(0,1fr)_30rem]">
        <SearchPane query={query} onAdd={onAdd} />
        <CartPane onPay={pay} />
      </div>
      <HoldModal
        open={holdOpen}
        onClose={() => setHoldOpen(false)}
        onHeld={() => {
          setHoldOpen(false);
          void holds.refetch();
        }}
      />
      <CustomerModal
        open={customerOpen}
        current={customer}
        onClose={() => setCustomerOpen(false)}
        onAttach={cart.attachCustomer}
        onDetach={cart.detachCustomer}
      />
    </div>
  );
}
